/** Body switcher for a single Contract Detail stage tab.
 *
 * Gate-centric model:
 *   - agent / report tabs → read-only rich body + notes-only comment box.
 *   - intermediate gate (gate_1/gate_2) → upstream roll-up + rolled-up notes
 *     + the gate's own body + group Approve (this gate row only) + comment.
 *   - final gate → gate synthesis + the binding FR-077 decision + comment.
 *   - summary → the aggregate ContractSummaryView.
 * Agent-stage approval is automatic in auto-flow (and served by
 * StageReviewPanel in HIL mode), so agent tabs carry no action row.
 */
import { AlertCircle, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GateReportExportButton } from '@/pages/jobs/GateReportExportButton';
import { ContractSummaryView } from './ContractSummaryView';
import { StageCommentBox } from './StageCommentBox';
import { StageActionRow } from './StageActionRow';
import { FinalGateDecision } from './FinalGateDecision';
import { extractReviewerNote } from '@/pages/jobs/stages/StageHistoryRowHelpers';
import { ReviewerNoteCallout } from '@/pages/jobs/stages/ReviewerNoteCallout';
import { GateGroupSummary } from '@/pages/jobs/stages/GateGroupSummary';
import { GateNotesRollup } from '@/pages/jobs/stages/GateNotesRollup';
import { GateWarningsResume } from '@/pages/jobs/stages/GateWarningsResume';
import { stageRole } from '@/pages/jobs/stages/gateGroups';
import { RichDocumentAssistantBody } from '@/pages/jobs/stages/RichDocumentAssistantBody';
import { RichDocumentBody } from '@/pages/jobs/stages/RichDocumentBody';
import { RichPartiesBody } from '@/pages/jobs/stages/RichPartiesBody';
import { RichClauseBody } from '@/pages/jobs/stages/RichClauseBody';
import { RichComplianceBody } from '@/pages/jobs/stages/RichComplianceBody';
import { RichSignatureBody } from '@/pages/jobs/stages/RichSignatureBody';
import { RichGate1Body, RichGate2Body } from '@/pages/jobs/stages/RichGateBody';
import { RichGateFinalBody } from '@/pages/jobs/stages/RichGateFinalBody';
import { RichBootstrapBody } from '@/pages/jobs/stages/RichBootstrapBody';
import { RawOutputFallback } from '@/pages/jobs/stages/RawOutputFallback';
import { isRichRenderEmpty } from '@/pages/jobs/stages/richEmpty';
import type { PausedStage } from '@/types/enums';
import type { ContractDetail } from './types';
import type { AgentResult, Job, GateReport } from '@/types';

const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

function findAgent(agents: AgentResult[], name: string): AgentResult | undefined {
  return agents.find((a) => a.agent_name === name);
}

function renderRich(active: string, row: AgentResult, jobId: string | null, onActivity?: () => void) {
  const output = obj(row.output);
  switch (active) {
    case 'document_assistant':     return <RichDocumentAssistantBody output={output} />;
    case 'document':               return <RichDocumentBody output={output} jobId={jobId} onTranslated={onActivity} />;
    case 'parties':                return <RichPartiesBody   output={output} />;
    case 'clause':                 return <RichClauseBody    output={output} />;
    case 'gate_1':                 return <RichGate1Body     output={output} />;
    case 'compliance':             return <RichComplianceBody output={output} />;
    case 'signature':              return <RichSignatureBody output={output} />;
    case 'gate_2':                 return <RichGate2Body     output={output} />;
    case 'gate':                   return <RichGateFinalBody output={output} />;
    case 'orchestrator_bootstrap': return <RichBootstrapBody output={output} />;
    default:                       return null;
  }
}

/** Rich body + raw-narrative/JSON fallbacks — reused by every non-summary tab. */
function StageOutput({ active, row, jobId, onActivity }: {
  active: string; row: AgentResult; jobId: string | null; onActivity?: () => void;
}) {
  const output = obj(row.output);
  const richEmpty = isRichRenderEmpty(active as PausedStage, output);
  const narrative = typeof output.narrative === 'string' ? output.narrative : '';
  const hasNarrative = narrative.trim().length > 0;
  return (
    <>
      {renderRich(active, row, jobId, onActivity)}
      {hasNarrative && (
        <details className="text-xs mt-3" open={richEmpty}>
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
            {richEmpty && <AlertCircle className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />}
            <span>
              {richEmpty
                ? "CMP didn't recognise this output shape — showing agent's raw response"
                : 'Show raw agent report'}
            </span>
          </summary>
          <pre className="mt-2 max-h-[600px] overflow-auto rounded border bg-muted/30 p-3 text-sm whitespace-pre-wrap font-sans leading-relaxed">
            {narrative}
          </pre>
        </details>
      )}
      <RawOutputFallback output={output} initiallyOpen={richEmpty && !hasNarrative} />
    </>
  );
}

interface Props {
  active: string;
  contract: ContractDetail;
  agents: AgentResult[];
  job: Job | null;
  report: GateReport | null;
  jobId: string | null;
  onActivity?: () => void;
  onCommentSaved?: () => void;
  /** Opens the metadata editor — surfaced at the top of the Final Report. */
  onEditMetadata?: () => void;
}

export function ContractStageBody({
  active, contract, agents, job, report, jobId, onActivity, onCommentSaved, onEditMetadata,
}: Props) {
  if (active === 'summary') {
    return <ContractSummaryView contract={contract} agents={agents} />;
  }

  const role = stageRole(active);
  const row = findAgent(agents, active);

  // Final gate: the binding decision must show even before the gate agent
  // row exists (e.g. mid-pipeline) as long as a report is available.
  if (role === 'final_gate') {
    const note = row ? extractReviewerNote(row.edits_json ?? null) : null;
    return (
      <div className="space-y-3">
        {note && <ReviewerNoteCallout note={note} at={row?.approved_at} />}
        {row && <StageOutput active={active} row={row} jobId={jobId} onActivity={onActivity} />}
        <FinalGateDecision job={job} report={report} />
        {jobId && (
          <StageCommentBox jobId={jobId} stage="gate" initialNote={note} onSaved={() => { onCommentSaved?.(); }} />
        )}
      </div>
    );
  }

  if (!row) {
    return (
      <Card className="border-dashed bg-muted/30">
        <CardContent className="text-center py-10 text-sm text-muted-foreground">
          The {active.replace(/_/g, ' ')} agent has not run for this contract yet.
        </CardContent>
      </Card>
    );
  }

  const note = extractReviewerNote(row.edits_json ?? null);
  const isGate = role === 'intermediate_gate';
  // Final report is a summary artefact — no approve / send-to-review. Its
  // create-document and edit-metadata actions sit at the top of the report.
  const isFinalReport = active === 'orchestrator_bootstrap';

  return (
    <>
      {isFinalReport && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b pb-3">
          <p className="text-sm font-medium">Final report</p>
          <div className="flex items-center gap-2">
            {jobId && <GateReportExportButton jobId={jobId} />}
            {onEditMetadata && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={onEditMetadata}>
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Edit metadata
              </Button>
            )}
          </div>
        </div>
      )}
      {note && <ReviewerNoteCallout note={note} at={row.approved_at} className="mb-3" />}
      {isGate && (
        <div className="mb-3 space-y-3">
          <GateGroupSummary gate={active} agents={agents} />
          <GateWarningsResume gate={active} agents={agents} />
          <GateNotesRollup gate={active} agents={agents} />
        </div>
      )}
      <StageOutput active={active} row={row} jobId={jobId} onActivity={onActivity} />
      {jobId && (
        <>
          {isGate && (
            <StageActionRow
              jobId={jobId}
              stage={active as PausedStage}
              row={row}
              onChanged={() => { onCommentSaved?.(); }}
            />
          )}
          <StageCommentBox
            jobId={jobId}
            stage={active}
            initialNote={note}
            onSaved={() => { onCommentSaved?.(); }}
          />
        </>
      )}
    </>
  );
}
